import { z } from 'zod';
import { type LinklyEnv, zLinklyEnv } from '../enums';

/**
 * The synthetic credentials the Linkly emulator (our Convex fake) accepts. Switching a device to
 * `emulator` stamps these directly (no network round-trip), so the "device always has a token"
 * invariant holds instantly. The emulator does no auth, so their exact values are irrelevant beyond
 * being matched by the emulator handler.
 */
export const LINKLY_EMULATOR_SECRET = 'virtualSecretAbcd';
export const LINKLY_EMULATOR_TOKEN = 'virtualTokenAbcd';
/** Mirrors the `expirySeconds` the emulator's token endpoint returns (6 hours). */
export const LINKLY_EMULATOR_TOKEN_EXPIRY_SECONDS = 21600;

/**
 * "Linkly device" may also be known as "PIN pad" or "EFTPOS terminal".
 */
export const linklyDeviceSchema = z.object({
  restaurantId: z.string(),
  /**
   * The Linkly `username` used when pairing a new device.
   */
  username: z.string().nullable().default(null),
  /**
   * The Linkly `password` used when pairing a new device.
   */
  password: z.string().nullable().default(null),
  /**
   * The temporary `pairCode` used when pairing a new device.
   */
  pairCode: z.string().nullable().default(null),
  /**
   * The iPad `uuid` this Linkly device belongs to. Used as the Linkly `posId` (a GUID) and to
   * lookup the POS `name`/`version` passed to the Linkly token endpoint. (Named `posTerminalUuid`
   * because it maps to Linkly's own `posId` terminology.)
   */
  posTerminalUuid: z.string().nullable().default(null),
  /**
   * `secret` is sensitive. DO NOT expose to the public.
   * The `secret` is returned when calling Linkly pairing endpoint.
   * It will be valid until the account password changes, or the PIN pad is paired to another POS.
   * Does not expire.
   */
  secret: z.string().nullable().default(null),
  /**
   * Error message if there was a problem generating the secret during device pairing.
   */
  secretGenerationError: z.string().nullable().default(null),
  secretGenerationErrorAt: z.number().nullable().default(null),
  /**
   * The time when the device was successfully paired (and `secret` generated).
   */
  secretGeneratedAt: z.number().nullable().default(null),
  /**
   * The auth token to be used when calling Linkly endpoints.
   * The `secret` can be used to generate a new `token` if expired.
   */
  token: z.string().nullable().default(null),
  /**
   * Derived from Linkly `expirySeconds` value when the token is generated.
   * Linkly docs: "Don't store 'expirySeconds', use it to calculate and store the POS local expiry date and time."
   * In milliseconds since the Unix epoch.
   */
  tokenExpiresAt: z.number().nullable().default(null),
  /**
   * Error message if there was a problem generating the token.
   */
  tokenGenerationError: z.string().nullable().default(null),
  /**
   * In sandbox mode, when the VPP pin pad pairs with a new POS terminal using FUNC + 8880 + OK,
   * any previously paired terminals lose authorization.
   * While refresh tokens can STILL be generated for unauthorized terminals, any API calls using these
   * tokens will fail with an HTTP 401 error. This can happen during testing when making auth
   * requests manually via Bruno/Postman. Actual production behaviour needs to be confirmed.
   */
  tokenGenerationErrorAt: z.number().nullable().default(null),
  /**
   * The time the token was last generated.
   */
  tokenGeneratedAt: z.number().nullable().default(null),
  /**
   * The venue's choice of whether this iPad uses integrated EFTPOS, under its `integrated-eftpos-australia`
   * feature flag (which OrderHub controls). If `true` and the flag is on, the "Pair EFTPOS" button
   * is visible on the POS side navigation bar. Turning it off keeps the credentials.
   */
  isEnabled: z.boolean().default(false),

  /** @deprecated TODO: Remove later */
  useOrderHubVpp: z.boolean().optional(),

  /**
   * Where this device routes its Linkly API calls. Single source of truth for host selection
   * (see `getLinklyUrl`). New devices default to `production` (real hardware) so nothing
   * accidentally routes test traffic; `emulator`/`sandbox` are opt-in via `setLinklyEnv`.
   */
  linklyEnv: zLinklyEnv.default('production').optional(),

  /**
   * If not `null`, then the device is not connected. It may have been un-paired by the user or have
   * connectivity issues.
   * TODO: We should run GET device status every min, until it comes back online. Also, allow re-pairing when offline.
   */
  offlineAt: z.number().nullable().default(null),

  /**
   * Information returned by the Linkly API GET `/status` endpoint.
   * See https://www.linkly.com.au/apidoc/REST/#status
   */
  status: z
    .object({
      bankDescription: z.string().nullish().nullable().default(null),
      timeout: z.number().nullable().default(null),
      pinPadSerialNumber: z.string().nullable().default(null),
      pinPadVersion: z.string().nullish().nullable().default(null),
      eftTerminalType: z.string().nullish().nullable().default(null),
      // The timestamp when the status was last updated. Only updated when values actually change.
      // Use this instead of root level `updatedAt` field for any `status` field changes.
      updatedAt: z.number(),
    })
    .nullable()
    .default(null),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  updatedAt: z.number().optional(),
});
export type LinklyDevice = z.output<typeof linklyDeviceSchema>;
export type LinklyDeviceInput = z.input<typeof linklyDeviceSchema>;

export const linklyDeviceStrictSchema = linklyDeviceSchema.safeExtend({
  posTerminalUuid: z.guid().nullable(),
});

export const linklyDeviceCvxSchema = linklyDeviceSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
  updatedAt: z.number(),
});
export type LinklyDeviceCvx = z.output<typeof linklyDeviceCvxSchema>;

/**
 * TEMPORARY MIGRATION SHIMS
 */

// TODO: Delete once `.optional()` is removed from `linklyEnv`
export const getLinklyDeviceLinklyEnv = (device: Pick<LinklyDevice, 'linklyEnv'>): LinklyEnv =>
  device.linklyEnv ?? 'production';
